import { computed, inject, Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { DocumentRecord } from "@models/annual-statements/document-record";
import { Company } from "@models/companies/company";
import { Document } from "@models/documents/document";
import { EntityChangeData } from "@models/documents/entity-change-data";
import { RelationshipType } from "@models/relationships/relationship-type.enum";
import { SecurityRegistryRecord } from "@models/shares/security-registry-record";
import { NgbModal, NgbModalOptions, NgbModalRef } from "@ng-bootstrap/ng-bootstrap";
import { patchState, signalState } from "@ngrx/signals";
import {
  ICompanyProfileTabKeys
} from "@pages/companies/components/company-profile-page/company-profile-page.component";
import {
  CompanyEditModalComponent
} from "@pages/companies/components/company-profile-page/components/company-edit-modal/company-edit-modal.component";
import {
  IOfficeholdersPatchModel,
  IOfficeholdersViewModel
} from "@pages/companies/models/company-officeholders-state.interface";
import {
  CompanyProfileLoadingKey,
  ICompanyDetailsViewModel,
  ICompanyProfileState
} from "@pages/companies/models/company-profile-state.interface";
import {
  IShareholdersPatchModel,
  IShareholdersViewModel
} from "@pages/companies/models/company-shareholders-state.interface";
import { OfficeholderGroupModel } from "@pages/companies/models/officeholder-group-model";
import {
  CommonActionsModalComponent,
  IActionButtonConfig
} from "@shared/components/commons/common-actions-modal/common-actions-modal.component";
import {
  BillingSigningContactModalComponent
} from "@shared/components/documents-related/billing-signing-contact-modal/billing-signing-contact-modal.component";
import {
  SecurityholderViewModel
} from "@shared/components/entity-profiles/entity-securityholders-view/securityholder-view-model";
import { CompaniesApiService } from "@shared/services/companies-api.service";
import { DocumentsApiService } from "@shared/services/documents-api.service";
import { EntityIdentityApiService } from "@shared/services/entity-identity-api.service";
import { NotesApiService } from "@shared/services/notes-api.service";
import { SecurityApiService } from "@shared/services/security-api.service";
import { doNothing } from "@shared/utils/doNothing";
import { isAllowedToDeleteDocuments } from "@shared/utils/is-allowed-to-delete-documents";
import { checkIsDocumentRecordInSigning } from "@shared/utils/is-document-in-signing";
import { getAnnualStatementsPath } from "@shared/utils/route-path-utils";
import { diff } from "deep-object-diff";
import { ToastrService } from "ngx-toastr";
import { combineLatest, map, Observable, of, tap } from "rxjs";
import { ModalFormsService } from "../../../forms/services/modal-forms.service";

const companyProfileInitialState: ICompanyProfileState = {
  loaders: {
    isCompanyLoading: false,
    isPendingDocumentsLoading: false,
    isIsXpmConnectedLoading: false,
    isShareholdersLoading: false,
  },

  isPendingChangesVisible: true,

  companyId: '',
  companyWithPendingChanges: new Company(),
  lodgedCompany: new Company(),

  pendingDocumentRecords: [],
  isCurrentChangesHighlighted: false,

  officeholders: {
    isPastOfficeholdersVisible: false,
    isShowPastOfficeholdersToggleVisible: false,
    officeholderDetails: null,
  },

  shareholders: {
    isLoaded: false,
    isPastShareholdersVisible: false,
    shareholdersWithPendingChanges: [],
    lodgedShareholders: [],
    shareholderDetails: null,
  },

  isXpmConnected: false,
  notesTotal: 0
};

@Injectable()
export class CompanyProfileStateService {
  private companiesApiService = inject(CompaniesApiService);
  private documentsApiService = inject(DocumentsApiService);
  private entityIdentityApiService = inject(EntityIdentityApiService);
  private securityApiService = inject(SecurityApiService);
  private notesApiService = inject(NotesApiService);

  private modalFormsService = inject(ModalFormsService);
  private modalService = inject(NgbModal);
  private router = inject(Router);
  private toastr = inject(ToastrService);

  state = signalState(companyProfileInitialState);

  // =====  Selectors  =====

  isModalOpened = this.modalFormsService.modalOpened;
  isLoading = computed(() => Object.values(this.state.loaders).some((isLoading) => isLoading));

  companyDetailsViewModel = computed<ICompanyDetailsViewModel>(() => {
    const state = this.state;
    const isPendingChangesVisible = state.isPendingChangesVisible();
    const companyWithPendingChanges = state.companyWithPendingChanges();
    const lodgedCompany = state.lodgedCompany();
    const viewModel: ICompanyDetailsViewModel = {
      lodgedCompany,
      companyWithPendingChanges,
      isPendingChangesVisible,
      companyDifference: null,
      isThereDifferenceBetweenCompany: false,
    };

    const isCompanyLoading = state.loaders.isCompanyLoading();

    if (isCompanyLoading) return viewModel;
    const companyDifference = diff(companyWithPendingChanges, lodgedCompany);
    const isThereDifferenceBetweenCompany = !!companyDifference;

    return {
      ...viewModel,
      companyDifference,
      isThereDifferenceBetweenCompany
    };
  });

  officeholdersViewModel = computed<IOfficeholdersViewModel>(() => {
    const isPendingChangesVisible = this.state.isPendingChangesVisible();
    const isPastOfficeholdersVisible = this.state.officeholders.isPastOfficeholdersVisible();
    const officeholders = this.state.companyWithPendingChanges.officers();
    const previousOfficeholders = this.state.lodgedCompany.officers();
    const officeholderDetails = this.state.officeholders.officeholderDetails();

    const officeholdersGroupsModels: OfficeholderGroupModel[] = [];

    const baseOfficeholdersForModels = isPendingChangesVisible
      ? officeholders
      : previousOfficeholders;

    baseOfficeholdersForModels.forEach((officeholder) => {
      const relatedGroupModel = officeholdersGroupsModels.find((model) => model.isRelatedOfficeholder(officeholder));

      if (relatedGroupModel) {
        relatedGroupModel.addRelationship(officeholder);
      } else {
        officeholdersGroupsModels.push(OfficeholderGroupModel.fromRelationship(officeholder));
      }
    });

    officeholdersGroupsModels.forEach((officeholderGroupModel) => officeholderGroupModel.addPreviousRelationships(previousOfficeholders));

    const appointedOfficeholdersGroupsModels: OfficeholderGroupModel[] = officeholdersGroupsModels.filter((officeholdersGroupModel) => !officeholdersGroupModel.isCeased());
    const ceasedOfficeholdersGroupsModels: OfficeholderGroupModel[] = officeholdersGroupsModels.filter((officeholdersGroupModel) => officeholdersGroupModel.isCeased());

    const isShowPastOfficeholdersToggleVisible = ceasedOfficeholdersGroupsModels.length > 0;

    const officeholdersViewModel: (string | OfficeholderGroupModel)[] = [
      'Current Officeholders',
      ...appointedOfficeholdersGroupsModels,
    ];

    if (isPastOfficeholdersVisible && ceasedOfficeholdersGroupsModels.length) {
      officeholdersViewModel.push(
        'Past Officeholders',
        ...ceasedOfficeholdersGroupsModels
      );
    }

    return {
      isPendingChangesVisible,
      officeholdersViewModel,
      isShowPastOfficeholdersToggleVisible,
      isPastOfficeholdersVisible,
      officeholderDetails
    };
  });

  shareholdersViewModel = computed<IShareholdersViewModel>(() => {
    const isPendingChangesVisible = this.state.isPendingChangesVisible();
    const isPastShareholdersVisible = this.state.shareholders.isPastShareholdersVisible();
    const shareholderDetails = this.state.shareholders.shareholderDetails();
    const shareholdersWithPendingChanges = this.state.shareholders.shareholdersWithPendingChanges();
    const lodgedShareholders = this.state.shareholders.lodgedShareholders();

    const viewModel = shareholdersWithPendingChanges.map((record) => {
      const model = new SecurityholderViewModel({ record });
      const relatedPreviousRecord = lodgedShareholders.find((previousRecord) => {
        return previousRecord.holders.every((previousHolder) => previousHolder.relationshipId == (record.holders.find((holder) => holder.relationshipId == previousHolder.relationshipId)?.relationshipId));
      });

      if (relatedPreviousRecord) {
        model.fillPreviousRecord(relatedPreviousRecord);
      }

      return model;
    });

    const currentShareholders = viewModel.filter((model) => !model.isPastShareholder());
    const pastShareholders = viewModel.filter((model) => model.isPastShareholder());

    const isShowPastShareholdersToggleVisible = pastShareholders.length > 0;

    const shareholders: (string | SecurityholderViewModel)[] = [
      'Current Shareholders',
      ...currentShareholders,
    ];

    if (isPastShareholdersVisible) {
      shareholders.push(
        'Past Shareholders',
        ...pastShareholders
      );
    }

    return {
      isPendingChangesVisible,
      isPastShareholdersVisible,
      isShowPastShareholdersToggleVisible,
      shareholderDetails,
      shareholders,
    };
  });

  // =====  State Mutations  =====

  setIsLoading(key: CompanyProfileLoadingKey, isLoading = true): void {
    patchState(this.state, (state) => ({ loaders: { ...state.loaders, [key]: isLoading } }));
  }

  onSwitchOnTab(tabName: ICompanyProfileTabKeys): void {
    switch (tabName) {
      case 'Shareholders' : {
        if (this.state.shareholders.isLoaded()) return;
        this.loadShareholders();
        break;
      }
    }
  }

  setIsPendingChangesVisible(isPendingChangesVisible = true): void {
    patchState(this.state, () => ({ isPendingChangesVisible }));
  }

  setNotesTotal(notesTotal: number): void {
    patchState(this.state, () => ({ notesTotal }));
  }

  patchOfficeholderState(officeholdersState: IOfficeholdersPatchModel): void {
    patchState(this.state, (state) => ({ officeholders: { ...state.officeholders, ...officeholdersState } }));
  }

  patchShareholdersState(shareholdersPatchModel: IShareholdersPatchModel): void {
    patchState(this.state, (state) => ({ shareholders: { ...state.shareholders, ...shareholdersPatchModel } }));
  }

  setIsCurrentChangesHighlighted(isCurrentChangesHighlighted: boolean): void {
    patchState(this.state, () => ({ isCurrentChangesHighlighted }));
  }

  // =====  Life Cycle  =====

  init(companyId: string): void {
    patchState(this.state, () => ({ companyId }));

    this.loadCompany();
    this.loadPendingDocuments();
    this.loadIsXpmConnected();
    this.loadNotesTotal();
  }

  // Data Loading

  loadCompany(): void {
    this.setIsLoading('isCompanyLoading');

    const loadCompanyWithPendingChangesRequest = this.getCompanyProfile();
    const loadLodgedCompanyRequest = this.getCompanyProfile(false);

    combineLatest(loadCompanyWithPendingChangesRequest, loadLodgedCompanyRequest)
      .subscribe({
        next: ([companyWithPendingChanges, lodgedCompany]) => {
          patchState(this.state, () => ({ companyWithPendingChanges, lodgedCompany }));
          this.setIsLoading('isCompanyLoading', false);
        },
        error: (error) => {
          console.warn('[state.loadCompany] error: ', error);
          this.toastr.error('Failed to load company profile', 'Error');
          void this.router.navigate(['/companies']);
        }
      });
  }

  loadPendingDocuments(documentWasCreated = false): void {
    this.setIsLoading('isPendingDocumentsLoading');

    this.getCompanyPendingDocuments()
      .subscribe({
        next: (pendingDocumentRecords) => {
          patchState(this.state, () => ({ pendingDocumentRecords }));
          this.setIsLoading('isPendingDocumentsLoading', false);

          if (documentWasCreated) this.setIsCurrentChangesHighlighted(true);
        },
        error: (error) => {
          console.warn('[state.loadDocuments] error: ', error);
          this.toastr.error('Failed to load company documents', 'Error');
          void this.router.navigate(['/companies']);
        }
      });
  }

  loadIsXpmConnected(): void {
    this.setIsLoading('isIsXpmConnectedLoading');

    this.getIsXpmConnected()
      .subscribe({
        next: (isXpmConnected) => {
          patchState(this.state, () => ({ isXpmConnected }));
          this.setIsLoading('isIsXpmConnectedLoading', false);
        },
        error: (error) => {
          console.warn('[state.loadIsXpmConnected] error: ', error);
          this.toastr.error('Failed to load XPM connection status', 'Error');
        }
      });
  }

  loadShareholders(): void {
    this.setIsLoading('isShareholdersLoading');

    const loadShareholdersWithPendingChangesRequest = this.getShareholders();
    const loadLodgedShareholdersRequest = this.getShareholders(false);

    combineLatest([loadShareholdersWithPendingChangesRequest, loadLodgedShareholdersRequest])
      .subscribe({
        next: ([shareholdersWithPendingChanges, lodgedShareholders]) => {
          const shareholderDetails = this.state.shareholders.shareholderDetails();
          const isPastShareholdersVisible = this.state.shareholders.isPastShareholdersVisible();
          const isShowPastShareholdersToggleVisible = shareholdersWithPendingChanges.some((record) => !record.holdings.length);

          patchState(
            this.state,
            () => ({
              shareholders: {
                isLoaded: true,
                isPastShareholdersVisible,
                shareholderDetails,
                isShowPastShareholdersToggleVisible,
                shareholdersWithPendingChanges,
                lodgedShareholders,
              }
            })
          );
          this.setIsLoading('isShareholdersLoading', false);
        },
        error: (error) => {
          console.warn('[state.loadShareholders] error: ', error);
          this.toastr.error('Failed to load shareholders', 'Error');
        }
      });
  }

  loadNotesTotal(): void {
    this.getNotesTotal()
      .subscribe({
        next: (notesTotal) => this.setNotesTotal(notesTotal),
        error: (error) => {
          console.warn('[state.loadNotesTotal] error: ', error);
          this.toastr.error('Failed to load total notes quantity', 'Error');
        }
      });
  }

  // =====  Modals  =====

  openEditSigningBillingContact(): void {
    const modalRef = this.modalService.open(BillingSigningContactModalComponent, { size: 'sm' });
    const instance = (modalRef.componentInstance as BillingSigningContactModalComponent);

    instance.company = this.state.lodgedCompany();
    instance.directors = this.state.companyWithPendingChanges().currentOfficers.filter((relationship) => relationship.type == RelationshipType.Director);
    instance.billingContact = this.state.companyWithPendingChanges().billingContact;
    instance.signingContact = this.state.companyWithPendingChanges().signingContact;

    modalRef.result.then(() => this.loadCompany(), doNothing);
  }

  openEditCompanyDetailsModal(): void {
    const modalRef = this.modalService.open(CompanyEditModalComponent);
    const instance = (modalRef.componentInstance as CompanyEditModalComponent);
    const copy = structuredClone(this.state.companyWithPendingChanges());

    instance.editModel = copy;

    instance.$confirm = () => {
      return this.updatedCompanyDetails(copy)
        .pipe(
          tap({
            next: () => {
              this.loadCompany();
              this.toastr.success('Changes saved', 'Success');
            },
            error: (error) => {
              this.toastr.error('Failed to save changes', 'Error');
              console.warn('[updatedCompanyDetails] error: ', error);
            }
          })
        );
    };
  }

  // =====  Forms  =====

  openDraftForm(documentRecord: DocumentRecord): void {
    if (this.modalFormsService.modalOpened()) return;
    this.getDocument(documentRecord)
      .subscribe({
        next: (document) => {
          document.company = this.state.companyWithPendingChanges();
          this.setDataUpdaterForModal(this.modalFormsService.openModalWithDocument(document, document.company, document.changes[0]));
        },
        error: (error) => {
          this.toastr.error('Failed to load document', 'Error');
          console.warn('[getDocument] error: ', error);
        }
      });
  }

  openModalWithCompany<T>(
    change: EntityChangeData,    // instance of changes in form
    isEdit = false,
    props: Partial<T> = {},      // props for form component
    modalOptions: NgbModalOptions = {},
    company = this.state.companyWithPendingChanges()
  ): NgbModalRef | undefined {
    if (this.modalFormsService.modalOpened()) return;
    return this.setDataUpdaterForModal(this.modalFormsService.openModalWithCompany(change, company, isEdit, props, modalOptions));
  }

  private setDataUpdaterForModal(modalRef: NgbModalRef): NgbModalRef {
    modalRef.result
      .then(() => {
          this.loadCompany();
          this.loadPendingDocuments(true);
        },
        doNothing
      );

    return modalRef;
  }

  // =====  Request Actions  =====

  tryDeleteDocuments(documentRecords: DocumentRecord[]): void {
    const isAsicStatusAllowsDeleteDocument = isAllowedToDeleteDocuments(documentRecords);

    if (!isAsicStatusAllowsDeleteDocument) {
      this.toastr.error("Lodged documents, or documents that were sent for lodgement cannot be deleted", "Error");
      return;
    }

    this.openDeleteDocumentModal(documentRecords);
  }

  openDeleteDocumentModal(documentRecords: DocumentRecord[]): void {
    const modalRef = this.modalService.open(CommonActionsModalComponent, { size: 'sm' });
    const componentInstance = modalRef.componentInstance as CommonActionsModalComponent;

    const isDocumentRecordInSigning = checkIsDocumentRecordInSigning(documentRecords);
    const isBulk = documentRecords.length > 1;
    const pluralS = isBulk ? 's' : '';
    const header = `Delete document${ pluralS }`;
    const theOnlyDocumentName = ` - ${ documentRecords[0].description }`;
    let body = `Are you sure you would like to delete the selected document${ isBulk ? pluralS : theOnlyDocumentName }?`;

    if (isDocumentRecordInSigning) {
      body += ` Deleting the document${ pluralS } will automatically delete the eSigning envelope. Would you like to continue?`;
    }

    const actions: IActionButtonConfig[] = [
      {
        text: 'Delete',
        action: of(true)
      }
    ];

    componentInstance.config = {
      ...componentInstance.config,
      header,
      body,
      actions,
      showCloseBtn: true,
      closeBtnText: 'Cancel'
    };

    modalRef.result
      .then(
        () => this.performDeleteDocuments(documentRecords),
        doNothing
      );
  }

  performDeleteDocuments(documentRecords: DocumentRecord[]): void {
    this.setIsLoading('isPendingDocumentsLoading');
    const request = documentRecords.length > 1
      ? this.deleteDocumentsList(documentRecords)
      : this.deleteDocument(documentRecords[0]);

    request
      .subscribe({
        next: () => {
          this.loadCompany();
          this.loadPendingDocuments();
          this.loadShareholders();
        },
      });
  }

  //for testing
  performGenerateFakeAnnualStatement(): void {
    this.toastr.info('Generating Annual Statement');

    this.generateFakeAnnualStatement()
      .subscribe({
        next: (annualStatementId) => {
          void this.router.navigate(getAnnualStatementsPath(annualStatementId), { queryParams: { step: 0 } });
          this.toastr.success('Annual statement generated', 'Success');
        },
        error: (error) => {
          console.warn('[performGenerateFakeAnnualStatement] error: ', error);
          this.toastr.error('Failed to generate annual statement', 'Error');
        }
      });
  }

  // =====  Requests  =====

  getCompanyProfile(applyIncompleteDocuments = true): Observable<Company> {
    const companyId = this.state().companyId;
    return this.companiesApiService.getById(companyId, applyIncompleteDocuments);
  }

  getCompanyPendingDocuments(): Observable<DocumentRecord[]> {
    const companyId = this.state().companyId;
    return this.documentsApiService.getEntityPendingDocuments(companyId)
      .pipe(
        map((result) => result.records)
      );
  }

  getIsXpmConnected(): Observable<boolean> {
    return this.entityIdentityApiService.isXpmConnected();
  }

  getShareholders(applyIncompleteDocuments = true): Observable<SecurityRegistryRecord[]> {
    const companyId = this.state().companyId;
    return this.securityApiService.getSecurityRegistryRecords(companyId, true, '', applyIncompleteDocuments);
  }

  getNotesTotal(): Observable<number> {
    const companyId = this.state().companyId;
    return this.notesApiService.getNotesTotal(companyId);
  }

  getDocument(documentRecord: DocumentRecord): Observable<Document> {
    return this.documentsApiService.getById(documentRecord.documentId)
      .pipe(
        map((document) => new Document(document))
      );
  }

  updatedCompanyDetails(company: Company, companyId = this.state.companyId()): Observable<any> {
    return this.companiesApiService.updateDetails(companyId, company);
  }

  deleteDocument(documentRecord: DocumentRecord): Observable<void> {
    return this.documentsApiService.deleteDocument(documentRecord.documentId);
  }

  deleteDocumentsList(documentRecords: DocumentRecord[]): Observable<void> {
    const documentsIds = documentRecords.map((documentRecord) => documentRecord.documentId);
    return this.documentsApiService.deleteDocumentsBulk(documentsIds);
  }

  generateFakeAnnualStatement(companyId = this.state.companyId()): Observable<string> {
    return this.companiesApiService.generateFakeAnnualStatement(companyId);
  }
}
